/**
 * Newsletter Signup Component
 * Simple email capture. No fluff.
 */

import { useState } from 'react';
import { api } from '../../lib/api';

interface NewsletterProps {
  variant?: 'inline' | 'card';
  source?: string;
}

export default function Newsletter({ variant = 'inline', source = 'footer' }: NewsletterProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.subscribeNewsletter({ email, source });
      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={variant === 'card' ? 'bg-green-50 p-4 border border-green-200' : ''}>
        <p className="text-small text-green-700">
          Thanks for subscribing! Check your email to confirm.
        </p>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div className="bg-surface-subtle p-6 border border-border-subtle">
        <h3 className="text-h4 font-medium text-text-primary">Stay updated</h3>
        <p className="mt-2 text-small text-text-secondary">
          Get insights on tax planning, real estate, and returning to India.
        </p>
        <form onSubmit={handleSubmit} className="mt-4">
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-2 text-small border border-border-subtle bg-white focus:outline-none focus:border-text-primary transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-small font-medium bg-text-primary text-white hover:bg-text-secondary transition-colors disabled:opacity-50"
            >
              {loading ? '...' : 'Subscribe'}
            </button>
          </div>
          {error && (
            <p className="mt-2 text-small text-red-600">{error}</p>
          )}
        </form>
      </div>
    );
  }

  // Inline variant (for footer)
  return (
    <div>
      <h4 className="text-caption font-medium uppercase tracking-wider text-text-tertiary mb-4">
        Newsletter
      </h4>
      <p className="text-small text-text-secondary mb-4">
        Updates on tax planning, real estate, and more.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="flex-1 px-3 py-2 text-small border border-border-subtle bg-white focus:outline-none focus:border-text-primary transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-small font-medium bg-text-primary text-white hover:bg-text-secondary transition-colors disabled:opacity-50"
          >
            {loading ? '...' : 'Join'}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-small text-red-600">{error}</p>
        )}
      </form>
    </div>
  );
}
